import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { channelStatesFromList } from '@/services/push/pushTarget';

// Fonte ÚNICA da permissão de notificação do app — push remoto e notificação de
// download pedem e consultam por aqui.
//
// Por que pelo LocalNotifications e não pelo plugin do Firebase:
//   - o `checkPermissions` do @capacitor-firebase/messaging devolve `granted`
//     SEMPRE abaixo do Android 13 (não olha `areNotificationsEnabled`), então
//     um aluno que desligou as notificações nos Ajustes aparecia como liberado;
//   - o do LocalNotifications consulta `areNotificationsEnabled` < 13 e a
//     permissão de runtime POST_NOTIFICATIONS >= 13, e no iOS lê o mesmo
//     `UNUserNotificationCenter` que o Firebase usa — é a mesma autorização;
//   - não depende do Firebase estar configurado (build sem
//     google-services.json / GoogleService-Info.plist continua com o download
//     notificando normalmente).
//
// Estados: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale', e
// 'unsupported' fora do app nativo.

// Importância Android: 4 = HIGH (aparece por cima da tela), 3 = DEFAULT (só na
// barra, com som). Resposta é conversa com o próprio aluno: HIGH. Aula nova fica
// em DEFAULT (N1), porque sairia por cima da tela até à noite. Certificado
// (sc-1616) também em DEFAULT: é raro, mas não pede ação imediata. Pagamentos
// (assinatura em atraso, sc-1616) em HIGH: é sobre perder o acesso, e o aluno
// precisa agir.
export const PUSH_CHANNELS = {
  replies: {
    id: 'respostas',
    name: 'Respostas aos seus comentários',
    description: 'Quando alguém responde um comentário seu',
    importance: 4
  },
  content: {
    id: 'conteudo_novo',
    name: 'Novas aulas e conteúdos',
    description: 'Quando um curso seu ganha aulas ou módulos novos',
    importance: 3
  },
  certificates: {
    id: 'certificados',
    name: 'Certificados',
    description: 'Quando o certificado de um curso seu fica disponível',
    importance: 3
  },
  payments: {
    id: 'pagamentos',
    name: 'Pagamentos',
    description: 'Quando há um problema no pagamento de uma assinatura sua',
    importance: 4
  }
};

// Categorias iOS do push (aps.category). Sem ações na v1: só o placeholder da
// prévia oculta, para a tela bloqueada não mostrar o nome do curso nem o trecho
// do comentário. ATENÇÃO: `registerActionTypes` chama `setNotificationCategories`,
// que SUBSTITUI todas as categorias do app. Hoje o app não registra nenhuma
// outra (as notificações de download não usam `actionTypeId`); quem precisar de
// categoria nova tem de entrar NESTA lista, não chamar o método de novo.
export const IOS_CATEGORIES = [
  { id: 'COMMENT_REPLY', iosHiddenPreviewsBodyPlaceholder: 'Nova resposta ao seu comentário' },
  { id: 'CONTENT_NEW', iosHiddenPreviewsBodyPlaceholder: 'Novas aulas disponíveis' },
  { id: 'CERTIFICATE_ISSUED', iosHiddenPreviewsBodyPlaceholder: 'Certificado disponível' },
  { id: 'SUBSCRIPTION_LATE', iosHiddenPreviewsBodyPlaceholder: 'Problema no pagamento da assinatura' }
];

const isNative = () => Capacitor.isNativePlatform();
const isAndroid = () => Capacitor.getPlatform() === 'android';
const isIos = () => Capacitor.getPlatform() === 'ios';

const listeners = new Set();
let _lastKnown = null;
let _requestPromise = null;

function notify (permission) {
  if (permission === _lastKnown) return;
  _lastKnown = permission;
  listeners.forEach(fn => {
    try { fn(permission); } catch (e) {}
  });
}

// Quem quer reagir a mudança de permissão (o registro do aparelho reenvia o
// estado ao backend). Devolve a função de remoção.
export function onPermissionChange (fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function checkNotificationPermission () {
  if (!isNative()) return 'unsupported';
  try {
    const { display } = await LocalNotifications.checkPermissions();
    notify(display);
    return display;
  } catch (e) {
    console.warn('[push] checkPermissions falhou:', e?.message || e);
    return 'prompt';
  }
}

// Pede só se ainda dá para pedir. `denied` não reabre o prompt do SO (o iOS não
// deixa e o Android 13+ para de mostrar depois de duas recusas) — nesse caso o
// caminho é `openNotificationSettings`.
export async function requestNotificationPermission () {
  if (!isNative()) return 'unsupported';
  // Single-flight: o download e o push podem pedir ao mesmo tempo, e dois
  // prompts do SO empilhados é bug visível.
  if (_requestPromise) return _requestPromise;
  _requestPromise = (async () => {
    const current = await checkNotificationPermission();
    if (current === 'granted' || current === 'denied') return current;
    try {
      const { display } = await LocalNotifications.requestPermissions();
      notify(display);
      return display;
    } catch (e) {
      console.warn('[push] requestPermissions falhou:', e?.message || e);
      return current;
    }
  })();
  try {
    return await _requestPromise;
  } finally {
    _requestPromise = null;
  }
}

// Ajustes de notificação DO APP (Android: ACTION_APP_NOTIFICATION_SETTINGS,
// que lista também os canais; iOS 16+: openNotificationSettingsURLString, antes
// disso a tela do app). Só a partir de gesto do usuário.
export async function openNotificationSettings () {
  if (!isNative()) return false;
  try {
    const { status } = await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.AppNotification
    });
    if (status) return true;
  } catch (e) {
    console.warn('[push] abrir ajustes de notificação falhou:', e?.message || e);
  }
  // Android 7.x (API 24-25) não tem a tela de notificações do app como intent
  // pública: cai na tela de detalhes do app, onde o toggle também está.
  if (isAndroid()) {
    try {
      const { status } = await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
      return Boolean(status);
    } catch (e) {}
  }
  return false;
}

// Canais Android do push, idempotente (recriar com o mesmo id só atualiza nome
// e descrição — importância e som congelam na 1ª criação: mudar a importância
// aqui só vale em instalação nova).
// Vibração explícita: nos plugins o default é false. Visibilidade PRIVATE na
// tela bloqueada, igual ao que o servidor manda no payload. Não toca nos canais
// de download (`ead_downloads` e o da FGS).
export async function ensurePushChannels () {
  if (!isNative() || !isAndroid()) return;
  for (const channel of [PUSH_CHANNELS.replies, PUSH_CHANNELS.content, PUSH_CHANNELS.certificates, PUSH_CHANNELS.payments]) {
    try {
      await LocalNotifications.createChannel({
        id: channel.id,
        name: channel.name,
        description: channel.description,
        importance: channel.importance,
        visibility: 0,
        vibration: true,
        lights: false
      });
    } catch (e) {
      // API < 26 não tem canal (rejeita com unavailable) — nada a fazer.
    }
  }
}

// { content, replies }: true = ativo, false = bloqueado pelo aluno
// (importância 0), null = desconhecido (iOS, API < 26, canal inexistente).
export async function getPushChannelStates () {
  if (!isNative() || !isAndroid()) return { content: null, replies: null };
  try {
    const { channels } = await LocalNotifications.listChannels();
    return channelStatesFromList(channels, { content: PUSH_CHANNELS.content.id, replies: PUSH_CHANNELS.replies.id });
  } catch (e) {
    return { content: null, replies: null };
  }
}

export async function registerPushCategories () {
  if (!isNative() || !isIos()) return;
  try {
    await LocalNotifications.registerActionTypes({ types: IOS_CATEGORIES });
  } catch (e) {
    console.warn('[push] registerActionTypes falhou:', e?.message || e);
  }
}
